import { Injectable } from '@angular/core';
import { IStringAnyMap } from 'src/app/app.types';
import { BehaviorSubject, interval, merge, Observable, of, Subject } from 'rxjs';
import { map, tap, catchError, switchMap, pairwise, filter } from 'rxjs/operators';
import { BooxService } from './boox.service';
import { StorageService } from './storage.service';
import { environment } from 'src/environments/environment';

import * as moment from 'moment';

import { jwtDecode } from "jwt-decode";
import { MatomoService } from './matomo.service';

export interface ISessionModel {
  session_token: string,
  session_id: string,
  id: number,
  name: string,
  first_name: string,
  last_name: string,
  email: string,
  is_sys_admin: boolean,
  last_login_date: string,
  host: string,
  role: string,
  role_id: number,
  impersonate ? : boolean,
}

export interface ISignInCredential {
  email ?: string,
  username ?: string,
  password : string,
} 

export interface IResetCredential {
  email : string,
  old_password ?: string,
  new_password ?: string,
  code ?: string,
} 

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  
  public $valid = new BehaviorSubject<boolean>(false);
  public $session = new BehaviorSubject<ISessionModel|null>(null);

  // session valid until is a observable that emits every second, the seconds until the session is valid
  public sessionValidUntil$ : Observable<number>;
  // session expired is a observable that emits when the session is expired
  public sessionExpired$ : Observable<boolean>;
  // session expired soon is a observable that emits when the session is expired soon
  public sessionExpiredSoon$ : Observable<boolean>;

  public get session () : ISessionModel | null {
    return this.$session.getValue();
  }

  public get isAuthenticated () : boolean {
    return !!this.session;
  }

  constructor(
    private storageSrv : StorageService,
    private booxSrv : BooxService,
    private Matomo : MatomoService,
  ) {
    this.$session.subscribe((session) => {
      this.$valid.next(!!session);
    })

    this.sessionValidUntil$ = interval(1000)
      .pipe(
        switchMap(() => {
          if(this.isAuthenticated && this.session){
            let _session = jwtDecode(this.session['session_token']);
            if(_session && _session['exp']){
              return of(moment.unix(_session['exp']).diff(moment(), 'seconds'));
            }
          }
          return of(0);
        }),
      );
    
    this.sessionExpiredSoon$ = this.sessionValidUntil$.pipe(
      map((_seconds) => _seconds < environment.sessionExpiredSoon && _seconds > 0),
    );

    this.sessionExpired$ = this.sessionValidUntil$.pipe(
      pairwise(),
      filter(([previous, current]) => previous > 0 && current <= 0),
      map(() => true),
    );

    this.sessionExpired$.subscribe((_expired) => this.signOut().subscribe());
  }

  public setSession (_session : string) {
    let booxConfig = this._booxConfig(environment.defaultBoox);
    if(!!_session) {
      this.storageSrv.setItem(booxConfig.sessionKey, _session);
      this.$session.next({
        ... this.session as ISessionModel,
        session_token : _session,
        session_id : _session,
      });
    }
  }

  /**
   * Get the boox config
   * @param _booxName Boox config name
   * @returns environment boox config 
   */
  private _booxConfig (_booxName : string){
    var booxName = _booxName as keyof typeof environment.boox;
    return environment.boox[booxName];
  }

  /**
   * Load the current session from local storage
   * @returns 
   */
  public load () : Observable<ISessionModel|null> {

    let booxConfig = this._booxConfig(environment.defaultBoox);

    if(!!this.storageSrv.getItem(booxConfig.sessionKey, false)){
      return this.booxSrv.get<ISessionModel>('user/session', {})
        .pipe(
          tap((_session:ISessionModel) => this.$session.next(_session )),
          tap(() => this.Matomo.trackEvent('session', 'load', 'success')),
          catchError((_error) => {
            this.storageSrv.clearItem(booxConfig.sessionKey);
            this.$session.next(null);
            this.Matomo.trackEvent('session', 'load', 'fail');
            return of(null);
          }),
        );
    }
    return of(null);
  }


  /**
   * Sign in 
   */
  signIn (_credentials : ISignInCredential) : Observable<ISessionModel|null> {
    
    let booxConfig = this._booxConfig(environment.defaultBoox);
    
    // clear current session
    this.storageSrv.clearItem(booxConfig.sessionKey);
    // 
    return this.booxSrv.post<ISessionModel>('user/session', _credentials, {})
      .pipe(
        tap((_session:ISessionModel) => this.storageSrv.setItem(booxConfig.sessionKey, _session['session_token'])),
        tap((_session:ISessionModel) => this.$session.next(_session)),
        tap(() => this.Matomo.trackEvent('session', 'sign-in', 'success')),
        catchError((_error) => {
          this.storageSrv.clearItem(booxConfig.sessionKey);
          this.$session.next(null);
          this.Matomo.trackEvent('session', 'sign-in', 'fail');
          throw _error.error;
        }),
      );
  }

  /**
   * Renew the current session
   * @returns 
   */
  renew () : Observable<ISessionModel|null> {

    let booxConfig = this._booxConfig(environment.defaultBoox);

    return this.booxSrv.put<ISessionModel>('user/session', {})
      .pipe(
        tap((_session:ISessionModel) => this.storageSrv.setItem(booxConfig.sessionKey, _session['session_token'])),
        tap((_session:ISessionModel) => this.$session.next(_session)),
        tap(() => this.Matomo.trackEvent('session', 'renew', 'success')),
        catchError((_error) => {
          this.storageSrv.clearItem(booxConfig.sessionKey);
          this.$session.next(null);
          this.Matomo.trackEvent('session', 'renew', 'fail');
          throw _error.error;
        }),
      );
  }

  /**
   * Sign Out
   */
  signOut () {

    let booxConfig = this._booxConfig(environment.defaultBoox);

    // 
    return this.booxSrv.delete('user/session', {})
      .pipe(
        catchError((_error) => of(null)),
        tap(() => this.storageSrv.clearItem(booxConfig.sessionKey)),
        tap(() => this.$session.next(null)),
        tap(() => this.Matomo.trackEvent('session', 'sign-out', 'success')),
      )
  }


  /**
   * reset password
   * @returns 
   */
  resetPassword (credentials : IResetCredential) : Observable<any> {
    return this.booxSrv.post('user/password', credentials, {params : {reset: true}})
      .pipe(
        tap(() => this.Matomo.trackEvent('session', 'reset-password', 'success')),
        catchError((_error) => {
          this.Matomo.trackEvent('session', 'reset-password', 'fail');
          throw _error.error;
        }),
      );
  }
}
